import { useState } from 'react';

import type {
  CultureSelectorCurrency,
  CultureSelectorLocale,
  CultureSelectorMarket,
  FetchedCultureData,
} from '@web-platform/global-components-types/culture';

export type OnCultureSelectorButtonClick = () => Promise<void>;

export type CultureSelectorContext = {
  locales: CultureSelectorLocale[];
  markets: CultureSelectorMarket[];
  currencies: CultureSelectorCurrency[];
  enMarkets: CultureSelectorMarket[];
  isFetchingCultureSelectorData: boolean;
  onCultureSelectorButtonClick: OnCultureSelectorButtonClick;
};

/**
 * The `useCultureSelectorData` hook is designed for fetching and managing culture data,
 * such as currencies, locales, and markets. It is primarily utilized in components requiring the dynamic fetching of such
 * data, like the Header when incorporating the culture selector modal.
 *
 * @public
 * @param fetchFunction - A function responsible for fetching the culture data; it gets passed in as it allows microsites complete freedom in deciding how and where they get their data from.
 * This way, they can either use the default function provided or write a custom solution that conforms more closely to the fetching norms already present in their codebase.
 * @param currentLocale - The current locale string used to initialize the default for the culture selection.
 * @param isPreReleasedLocales - A boolean flag to indicate if the pre-released locales should be included.
 *
 * @returns An object containing:
 *   - `currencies`: An array of currency objects.
 *   - `locales`: An array of locale objects.
 *   - `markets`: An array of market objects.
 *   - `enMarkets`: An array of English markets, used in the Switch To English button.
 *   - `isFetching`: A boolean flag indicating if the data is currently being fetched.
 *   - `onCultureSelectorButtonClick`: A callback function used to handle the lazy loading of the culture selector on a button click event.
 *
 * @example
 * ```tsx
 * import { useCultureSelectorData, fetchCultureSelectorData } from '@skyscanner-internal/global-components/culture-selector/helpers';
 *
 * const {
 *   currencies,
 *   locales,
 *   markets,
 *   enMarkets,
 *   isFetching,
 *   onCultureSelectorButtonClick,
 * } = useCultureSelectorData(fetchCultureSelectorData, currentLocale, isPreReleasedLocales);
 *
 *   // Utilize the fetched data in your component...
 *
 *   props.cultureSelectorProps.currencies = currencies;
 *   props.cultureSelectorProps.markets = markets;
 *   props.cultureSelectorProps.locales = locales;
 *   props.cultureSelectorProps.enMarkets = enMarkets;
 *
 *   <Header
 *       {...props}
 *       onCultureSelectorButtonClick={onCultureSelectorButtonClick}
 *       isFetchingCultureSelectorData={isFetching}
 *     />
 * }
 * ```
 *
 * @note
 * - Ensure that only the `fetchCultureSelectorData` and the other needed dependencies are imported to avoid unnecessarily increasing the bundle size.
 * - The hook should be used within a React functional component that follows the Rules of Hooks.
 *   For example:
 *   import { fetchCultureSelectorData, useCultureSelectorData } from '@skyscanner-internal/global-components/culture-selector/helpers';
 */

const useCultureSelectorData = (
  fetchCultureData: (localeName: string, showPreRelease: boolean) => Promise<FetchedCultureData>,
  localeName: string,
  showPreRelease: boolean,
): CultureSelectorContext => {
  const [currencies, setCurrencies] = useState<CultureSelectorCurrency[]>([]);
  const [locales, setLocales] = useState<CultureSelectorLocale[]>([]);
  const [markets, setMarkets] = useState<CultureSelectorMarket[]>([]);
  const [enMarkets, setEnMarkets] = useState<CultureSelectorMarket[]>([]);

  const [isFetching, setIsFetching] = useState(false);

  const onCultureSelectorButtonClick = async () => {
    const isAllDataFetched = [currencies, locales, markets, enMarkets].every(
      (data) => data.length > 0,
    );
    if (isAllDataFetched) return;

    setIsFetching(true);

    let fetchedData;
    try {
      fetchedData = await fetchCultureData(localeName, showPreRelease);
    } catch (error) {
      setIsFetching(false);
      throw error;
    }
    setIsFetching(false);

    setLocales(fetchedData.localesData);
    setMarkets(fetchedData.marketsData);
    setCurrencies(fetchedData.currenciesData);
    setEnMarkets(fetchedData.enMarketsData);
  };

  return {
    locales,
    markets,
    currencies,
    enMarkets,
    isFetchingCultureSelectorData: isFetching,
    onCultureSelectorButtonClick,
  };
};

export default useCultureSelectorData;
